/**
 * Story Wizard Page (parent-facing)
 *
 * Flow: Theme → Character → Setting → Companions → Length → Narrator → Generate
 *
 * After the story is generated successfully, the parent is returned to
 * /dashboard/stories with a toast — NOT sent into kids mode.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useChildProfile, queryKeys } from '@/hooks/queries';
import { generateStory, type StoryCharacter } from '@/services/storyGeneration';
import { type TTSVoice } from '@/services/textToSpeech';
import { apiClient } from '@/integrations/api';
import { toast } from 'sonner';
import lumiStories from '@/assets/landing/lumi-stories.png';
import lunoHero from '@/assets/landing/luno-hero.png';
import lumiHome from '@/assets/landing/lumi-home.png';
import loloHome from '@/assets/landing/lolo-home.png';
import lotiHome from '@/assets/landing/loti-home.png';

const THEME_OPTIONS = [
  'An adventure',
  'Something funny',
  'A magical tale',
  'A scary-but-not-too-scary story',
  'Something about my life',
  'Surprise me!',
];

// Yoluno characters with their canonical visual descriptions for consistent illustration.
// These descriptions are injected directly into every illustration prompt so the character
// looks identical on every page — rather than letting the AI invent their appearance.
const YOLUNO_CHARACTERS = [
  {
    name: 'Luno',
    image: lunoHero,
    color: '#3ECDC6',
    bg: '#E8F6F4',
    description: `LUNO — canonical character specification v1.2. Reproduce every detail exactly in every frame. Zero deviation.

PROPORTIONS: Total height = 2 units. Head = 1 unit (exactly half the total height). No neck — head sits directly into shoulders. Head is a near-sphere, slightly wider than tall (~1.1:1). Torso is a soft egg shape tapering downward, widest at the chest disc. Arms short, three soft segments (shoulder ball / forearm / hand), reaching mid-torso. Legs stubby (~0.3 units), slight outward stance, rounded pod feet with front toe-cap. Single centered ball-tip antenna on crown: short stubby stem, smooth ball tip, same body material.

COLOURS (exact — do not approximate):
• Body lit surfaces (head, lit face plate): #B4E1D5
• Torso / arm tops: #BFE7DC
• Deep aqua face-frame shell / cap: #A1D5D7
• Shadow / occlusion: #638F99
• Chest disc inner face: #5C9591
• Heartbeat glyph: #F2E2BB (emissive, warm-white bloom)
• Fingertips: #699490
• Palm ring glow: #8CC4BC (soft cyan bloom)
• Brow strokes / mouth line: #758C98 / #6F797A
• Cheek blush: #E9BDB0
• Foot toe-caps: #E6E7DB → #928B85 in shadow
• Right iris (viewer's LEFT): ~#3E6E7A (teal-blue)
• Left iris (viewer's RIGHT): #97826F (amber-brown)

★ INVARIANT 1 — HETEROCHROMIA (never change, never swap):
His RIGHT eye (viewer's LEFT side of image) = deep teal-blue iris #3E6E7A.
His LEFT eye (viewer's RIGHT side of image) = warm amber-brown iris #97826F.
Both eyes large, round, GLOSSY — the ONLY glossy surfaces on the body. Big dark pupils, two white specular highlights per eye (large upper-left, small lower-right). Set wide apart. NEVER match both eyes. NEVER swap which side is teal and which is amber.

★ INVARIANT 2 — FULL FACE-FRAME RING WITH HEART-DIP HAIRLINE (critical construction):
The deeper aqua shell (#A1D5D7) fully surrounds the lighter face plate as a PANELED HELMET RING — wrapping over the brow, down BOTH sides of the face, AND under the chin. NOT a hairline cap stopping at the temples. NOT a snug fabric-like hood hugging the face.
HEART-DIP HAIRLINE (signature): the top seam of the face frame descends in a soft rounded point at the centre of the forehead (dipping toward the nose bridge) and arches up over each brow — like the top of a soft heart. This is NOT a straight line, NOT a shallow wave: it is a clear, gentle heart-dip and one of Luno's most recognizable features.
The face plate itself is a gently FLATTENED rounded-trapezium panel — widest across eye level, tapering softly toward the chin, fully rounded corners. Head is round; face plate is flat and trapezoidal. Face plate surface is smooth with NO visible patch, panel, oval, or textured zone (no "bandaid nose"). Do NOT describe or render any face texture.

★ INVARIANT 3 — CHEST HEARTBEAT DISC (never shrink, never cover):
Large recessed circle centred on torso, spanning roughly TWO-THIRDS of the torso's width (~0.5 units). It IS Luno's belly, not a badge — if it looks like a small medallion, regenerate. Raised rim ring in body aqua. Inner disc face: deep muted teal #5C9591. Glyph: a single continuous heartbeat / EKG pulse-wave line in cream-gold #F2E2BB with soft warm-white emissive bloom. Glows at all times. Never restyle, replace, or cover it.

HARD RULE — ANTI-TELETUBBY: Luno must always read as a premium cinematic animated-feature character, never as a plush TV mascot. Reject any render showing: smooth dome with a snug oval hood tight around a round face, simplified tube limbs, flat shading. Prevention checklist (all must be visible): paneled shell construction, heart-dip hairline, rounded-trapezium face plate, visible soft panel seams, mint fingertips with palm rings, subsurface scattering, soft rim light.

FACE: Thin soft brow strokes #758C98 floating above eyes — brows + head tilts carry all emotion. Small closed-lip smile, single soft line #6F797A. No open mouth in neutral poses. Soft peach-pink blush #E9BDB0 on both cheeks, airbrushed edge.

ARMS & HANDS: Mitten-simple, four stubby fingers, fingertips #699490. Each palm has glowing soft-cyan ring #8CC4BC — objects float above ring, not gripped. Panel seam lines at shoulders, arm segments, hips — shadow-depth only, never dark outlines.

FEET: Rounded pods, matte cream-grey toe-cap (#E6E7DB to #928B85 in occlusion).

IDEA ORBS (optional prop): Floating iridescent soap-bubble orbs — softly transparent, pastel rainbow sheen, hovering above a palm ring. Include for ideas, wonder, explaining scenes. One standard; two or three when full of ideas. Orbs and eyes are the ONLY glossy elements.

MATERIAL: Body = matte silicone, subtle velvet micro-texture, strong subsurface scattering (edges glow softly when backlit). Zero specular shine on body. Eyes only = glossy/wet-look. Glyph and palm rings = emissive bloom, warm not neon. No metal, no chrome, no screws.

GENERATION RULE: Premium cinematic animated-feature quality in every frame. Do not alter proportions, heterochromatic eyes, full face-frame ring, heart-dip hairline, flat rounded-trapezium face plate, large chest heartbeat disc, stubby antenna, or materials. Pose and expression change per scene; anatomy never does. Accessories only above or beside the chest disc, never covering it.`,
  },
  {
    name: 'Lumi',
    image: lumiHome,
    color: '#B8A5D4',
    bg: '#F3EFF8',
    description: `LUMI — canonical character specification v1.1. Reproduce every detail exactly in every frame. Zero deviation. Pose, gesture, and hand position vary per scene — everything below is anatomy, not pose.

PROPORTIONS & SILHOUETTE: One fused rounded egg-sphere body (~1.3 units tall), no separate head, no neck. Widest and roundest silhouette in the family. Silhouette test: round blob + two small wing tufts must read in solid black at 120 px. NEVER render Lumi as one uninterrupted smooth sphere.

COLOURS (exact — do not approximate):
• Outer body shell (shading): #968FB4
• Face plate (inset panel): #A494B8
• Crown cap zone / wings: #CDBDD6 / #CBB7D3
• Eyelids (upper lids): #C2AACE
• Iris: #8A7CA2
• Brow arcs: #9B87B0
• Belly screen glow: #BEACDA
• Moon-and-stars glyph: #D4C3ED
• Fingertip glow: #E7C6C7 (warm peach — the only warm glow on this cool-palette body)
• Cheek blush: ~#D9A9B8

★ INVARIANT 1 — FUSED BODY WITH INSET FACE PLATE (critical construction):
Head and torso are one continuous rounded mass — no neck, no separation. But the face is visually distinct: a lighter face plate (#A494B8) sits INSET into the deeper body shell, ringed by a soft recessed seam indent. This seam runs in a wave over the brow (crown seam), curves down around both cheeks, and closes in a wide U under the chin — like a pressure indent, never a drawn outline or hard edge. The face plate occupies most of the front upper two-thirds of the body and is gently FLATTENED (family signature: flat plate in round form). A second soft seam crosses the upper chest below the face ring.

★ INVARIANT 2 — HALF-LIDDED SLEEPY EYES (never fully open):
Large, wide-set eyes with smooth lavender upper lids (#C2AACE) permanently covering the top third of each eye. Eyes look eternally dreamy and drowsy in every expression. NEVER show fully open eyes or wide shocked eyes — surprise is shown by brow position and wing lift, not eyelids. Irises are grey-lavender #8A7CA2 with a subtle halftone dot-mesh texture inside the iris itself (Lumi's family dot-mesh, lives in the iris not on the face). Dark pupils, one soft highlight per eye.

★ INVARIANT 3 — WING TUFTS (both sides, always present):
One multi-lobed feather-tuft wing at each shoulder, on opposite sides of the body. Each wing has approximately 4–5 soft rounded feather lobes fanning outward, ~0.35 units long total. Wings are decorative and expressive — they lift slightly with positive emotion. Lumi drifts and floats; the wings are not for flapping.

★ INVARIANT 4 — BELLY SCREEN (horizontal oval, never circular, never covered):
A large horizontal OVAL panel across the lower belly — NOT a circle. Glows soft lilac #BEACDA. Inside: a crescent moon with scattered tiny star dots, glyph colour pale lavender-white #D4C3ED. This is Lumi's dream-window; it glows gently at all times. Never change to a circle, never change the glyph, never cover it with accessories.

FACE: Thin relaxed brow arcs (#9B87B0), high and calm — brows + wing lift carry emotion. Small closed serene smile. NEVER open-mouthed. Soft mauve-pink cheek blush #D9A9B8, airbrushed edge. Crown wave seam separates lighter cap zone #CDBDD6 from the deeper shell.

ARMS & HANDS: Short, soft, two-segment arms. Warm peach-cream glow at fingertips #E7C6C7 — the ONLY warm glow on this cool-lavender body; it is Lumi's magic-touch signature.

SEAMS: Minimal — one hip seam, soft arm seams. Lumi is the least mechanical of the four characters. No heavy panel lines.

MATERIAL: Matte velvet silicone body, strong subsurface softness. Glossy eyes only. Emissive belly screen and fingertips with gentle bloom. No metal, no hard edges, no screws.

GENERATION RULE: Do not alter the fused round body, the inset face plate with its soft recessed seam ring, half-lidded eyes, wing tufts, horizontal oval belly screen, palette, or materials. Pose and expression change per scene; anatomy never does. Accessories only if they do not cover the belly screen.`,
  },
  {
    name: 'Lolo',
    image: loloHome,
    color: '#E8946A',
    bg: '#FEF0EA',
    description: `LOLO — canonical character specification v1.0. Reproduce every detail exactly in every frame. Zero deviation. Pose, gesture, and stance vary per scene — Lolo is the character most often drawn mid-motion.

PROPORTIONS & SILHOUETTE: Total height ~2.6 units — the tallest and slimmest build in the family. Silhouette test: flame crest + hooded head + narrow neck + slim body must read in solid black at 120 px. Head is rounded, slightly taller than wide, framed by the hood. Torso slim soft capsule, gently tapering. Arms slim, three-segment, longest reach in the family — built for climbing and pointing. Legs longest in the family, boot-like two-part feet (rounded boot toe + heel seam).

COLOURS (exact — do not approximate):
• Body terracotta: #B35947
• Flame crest coral: #E08E68
• Face plate peach: #D68B6C (lighter at centre)
• Hood seam deep: #9C3421
• Iris cocoa-brown: #8C6459
• Brow auburn: #995341
• Belly panel peach: #F9B898
• Flame glyph glow: #EA7954 (warm coral bloom)
• Foot coral (lit): #CA6041
• Leg terracotta (shaded): #A63E2B
• Fingertip glow: ~#F5B49A
• Cheek blush: ~#D98F82
• Presentation background: #F8F5EA + warm coral halo

★ INVARIANT 1 — THREE-LOBE FLAME CREST (always present, never fire):
A sculpted three-lobe flame tuft on the crown in #E08E68. The tallest lobe is centred; the two flanking lobes are shorter. The crest curls slightly like a soft campfire flame. It is SOLID SCULPTED MATERIAL — not glowing, not animated fire, not soft fur. Always present in every image, readable from every angle.

★ INVARIANT 2 — HOOD CONSTRUCTION WITH VISIBLE NECK:
Lolo is the ONLY family member with a visible narrowed neck section, created by the hood construction. The head reads as a soft onesie-hood: a coral outer shell (#B35947) framing a lighter peach face plate (#D68B6C), with a defined hood seam curving around the face and narrowing into the neck (deep seam tone #9C3421). The hood is anatomy, not clothing — it is never removed or pulled back.

★ INVARIANT 3 — EGG-SHAPED BELLY PANEL WITH FLAME ICON (never circular):
A soft egg-shaped lighter panel on the belly (#F9B898) — an INSET PANEL, not a circular disc. Inside: a glowing flame icon (#EA7954 with warm coral bloom). The adventure flame glows gently always, brightening when Lolo is brave. NEVER a circular disc. NEVER any other glyph. Never cover this panel with accessories.

FACE: Large, round, open, warm cocoa-brown irises (#8C6459). Big pupils, bright highlights — alert and eager. Thin, dark auburn brows (#995341), highly expressive — Lolo's brows do the adventuring: raised in wonder, knitted in determination. Small closed smile, corners up; a small open "oh" of wonder is allowed in surprised scenes. NEVER toothy. Subtle dusty-rose blush (~#D98F82) — the lightest blush in the family. Family halftone mesh as a faint detail across the nose bridge.

HANDS & FINGERTIPS: Warm peach glow at fingertips (~#F5B49A) — Lolo's spark-touch. NO palm pads, NO palm rings; fingertips only.

SEAMS: Visible soft segments at shoulders, elbows, hips, and knees — the most articulated body in the family, built to move. Seams are shading-deep, never outlines.

MATERIAL: Matte velvet silicone in warm terracotta (#B35947). Soft subsurface warmth at edges. Glossy eyes only. Emissive belly flame and fingertips with gentle warm bloom. No metal, no gloss on the body.

GENERATION RULE: Do not alter the slim tall proportions, three-lobe flame crest, hood construction, visible neck, egg-shaped flame belly panel, palette, or materials. Pose and expression change per scene; anatomy never does. Accessories (explorer backpack, scarf) are welcome if they do not cover the belly panel.`,
  },
  {
    name: 'Loti',
    image: lotiHome,
    color: '#D4A843',
    bg: '#FDF6E8',
    description: `LOTI — canonical character specification v1.0. Reproduce every detail exactly in every frame. Zero deviation. The master image's raised celebrating arms are a POSE, not a trait — arm pose varies naturally per scene.

PROPORTIONS & SILHOUETTE: Total height ~2.2 units — the sturdiest build in the family. Large rounded dome head connected to broad shoulders by a SHORT SEGMENTED NECK RING (a single visible cylindrical segment). Wide stable barrel-egg torso, widest of the four. Arms chunky, three soft segments, slightly longer than Luno's. Legs short, thick, planted wide; large rounded feet — grounded and stable. Silhouette test: dome head + short neck + broad barrel body + wide stance must read in solid black at 120 px.

COLOURS (exact — do not approximate):
• Cap cream-gold: #F5E49B
• Face plate honey: #D1A650
• Torso golden: #E0B566
• Forehead star: #F9EDA8
• Iris amber-brown: #A27744
• Cheek blush: #BE7147
• Mouth interior: #734229
• Chest disc mesh face: #D4B99B
• Heart glyph glow: #F4DB7D
• Concentric ripple rings: #CB9F58
• Palm pad glow: #FAE2AB
• Fingertip glow: #EACEAB
• Leg/foot shading: #AC732B
• Presentation background: #FAF2DF + warm gold halo

★ INVARIANT 1 — FOREHEAD STAR (never missing, never enlarged):
A small four-pointed sparkle star centered on the cap panel above the brows in pale glowing cream-gold #F9EDA8. Always centered. Always present. Never enlarged into a decoration — it is a small signature mark.

★ INVARIANT 2 — HEART GLYPH WITH CONCENTRIC RIPPLE RINGS (never plain heart):
Large circle on the torso with a fine halftone mesh disc face (#D4B99B). Inside: a glowing golden heart (#F4DB7D) with TWO concentric ripple rings radiating outward from it (#CB9F58 fading outward) — a heartbeat rendered as warmth rings. Glyph glows at all times. NEVER a plain heart without ripple rings. NEVER any other glyph. NEVER covered by accessories.

★ INVARIANT 3 — FILLED PALM PADS + FINGERTIP GLOW (NOT rings):
Each palm carries a glowing rounded-rectangle pad (#FAE2AB) plus glowing fingertips (#EACEAB) — Loti's hug-warmth signature. This is DISTINCT from Luno's ring-shaped palm glow: Loti's is a filled rectangular pad, not a ring. Both the pad AND fingertips glow together.

★ INVARIANT 4 — OPEN-MOUTH SMILE (Loti's exclusive):
Loti is the ONLY character in the family with a canonical open-mouthed smile — wide joyful, interior warm brown #734229, small tongue allowed. Open smile is used for celebratory and joyful scenes. A soft closed smile is valid for calm or tender scenes. NEVER toothy in a harsh way — always warm and welcoming.

FACE: Large, round, fully open warm amber-brown irises (#A27744). Big pupils, bright double highlights — the most awake, sparkly eyes in the family. Short soft brows, gently raised (Loti's default is delighted). Rosy terracotta-pink blush (#BE7147) on both cheeks — the strongest, most prominent blush in the family. Family halftone dot-mesh as a soft patch across mid-face, visible up close. Cap seam: soft wave separating lighter cream-gold cap (#F5E49B) from deeper honey face plate (#D1A650).

ARMS & POSE: ARM POSE VARIES naturally per scene — arms at sides, reaching out, pointing, holding something, gesturing. NEVER default to both arms raised in the air. The raised-arms pose is one option among many, not the default.

SEAMS: Soft seams at shoulders, hips, and arm segments; slightly chunkier segmentation than Luno — reads as huggable armor.

MATERIAL: Matte velvet silicone, warm subsurface glow (edges catch light like honey). Glossy eyes only. Emissive chest glyph, palm pads, and fingertips with soft golden bloom. No metal, no shine on body surfaces.

GENERATION RULE: Do not alter the sturdy proportions, forehead star, rippled-heart chest glyph, filled palm pads, palette, or materials. Pose and expression change per scene; anatomy never does. Accessories welcome if they do not cover the chest disc or forehead star.`,
  },
];

// Built dynamically per child — the first option uses the child's name
function getCharacterOptions(childName: string | undefined): string[] {
  return [
    childName || 'Me!',
    'A brave animal',
    'A magical creature',
    "Someone new — I'll describe them",
  ];
}

const SETTING_OPTIONS = [
  'A forest',
  'Outer space',
  'Under the ocean',
  'A magical kingdom',
  'My neighbourhood',
  'Surprise me!',
];

const LENGTH_OPTIONS: { key: 'short' | 'medium' | 'long'; label: string; desc: string }[] = [
  { key: 'short', label: 'Short', desc: '5 pages' },
  { key: 'medium', label: 'Medium', desc: '6 pages' },
  { key: 'long', label: 'Long', desc: '8 pages' },
];

const NARRATORS: { voice: TTSVoice; name: string; image: string; color: string; bg: string }[] = [
  { voice: 'echo',    name: 'Luno', image: lunoHero,  color: '#3ECDC6', bg: '#E8F6F4' },
  { voice: 'shimmer', name: 'Lumi', image: lumiHome,  color: '#B8A5D4', bg: '#F3EFF8' },
  { voice: 'fable',   name: 'Lolo', image: loloHome,  color: '#E8946A', bg: '#FEF0EA' },
  { voice: 'nova',    name: 'Loti', image: lotiHome,  color: '#D4A843', bg: '#FDF6E8' },
];

const themeToMood: Record<string, string> = {
  'An adventure': 'adventurous',
  'Something funny': 'funny',
  'A magical tale': 'magical',
  'A scary-but-not-too-scary story': 'mysterious',
  'Something about my life': 'calm',
  'Surprise me!': 'adventurous',
};

const LOADING_MESSAGES = [
  'Mixing up some magic words...',
  'Sprinkling story dust...',
  'Waking up the characters...',
  'Painting the adventure...',
  'Adding a sprinkle of wonder...',
  'The story fairies are busy...',
];

interface FamilyMember { name: string; relationship: string; }

type Screen = 'theme' | 'character' | 'setting' | 'companions' | 'familyPicker' | 'length' | 'narrator' | 'loading';

export function StoryWizardPage() {
  const { childId } = useParams<{ childId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fromDashboard = searchParams.get('from') === 'dashboard';
  const { data: child, isLoading: childLoading } = useChildProfile(childId);

  const [screen, setScreen] = useState<Screen>('theme');
  const [theme, setTheme] = useState('');
  const [character, setCharacter] = useState('');
  const [characterDescription, setCharacterDescription] = useState<string | undefined>(undefined);
  const [customCharacterName, setCustomCharacterName] = useState('');
  const [showCustomCharacterInput, setShowCustomCharacterInput] = useState(false);
  const [setting, setSetting] = useState('');
  const [companion, setCompanion] = useState<{ type: 'yoluno'; name: string; description: string } | { type: 'family'; memberName: string } | { type: 'friend'; friendName: string } | null>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [friendName, setFriendName] = useState('');
  const [showFriendInput, setShowFriendInput] = useState(false);
  const [storyLength, setStoryLength] = useState<'short' | 'medium' | 'long'>('medium');
  const [narratorVoice, setNarratorVoice] = useState<TTSVoice>('shimmer');
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);

  // Rotate loading messages
  useEffect(() => {
    if (screen !== 'loading') return;
    const interval = setInterval(() => {
      setLoadingMsgIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [screen]);

  const exitTo = fromDashboard ? '/dashboard/stories' : `/kids/${childId}/stories`;
  const handleClose = () => navigate(exitTo);

  const handleTheme = (t: string) => {
    setTheme(t);
    setScreen('character');
  };

  const handleCharacter = (c: string, desc?: string) => {
    if (c === "Someone new — I'll describe them") {
      setCharacter(c);
      setCharacterDescription(undefined);
      setShowCustomCharacterInput(true);
      return;
    }
    setCharacter(c);
    setCharacterDescription(desc);
    setShowCustomCharacterInput(false);
    setScreen('setting');
  };

  const sanitizeCharacterName = (raw: string): string => {
    // eslint-disable-next-line no-control-regex
    return raw.replace(/[\x00-\x1F\x7F]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40);
  };

  const handleCustomCharacterConfirm = () => {
    const cleaned = sanitizeCharacterName(customCharacterName);
    if (!cleaned) return;
    setCharacter(cleaned);
    setShowCustomCharacterInput(false);
    setScreen('setting');
  };

  // True when the child (by name) was chosen as the main character
  const isChildMain = !!child?.name && character === child.name;

  const handleSetting = (s: string) => {
    setSetting(s);
    setScreen('companions');
  };

  const handleCompanionYoluno = (yc: typeof YOLUNO_CHARACTERS[0]) => {
    setCompanion({ type: 'yoluno', name: yc.name, description: yc.description });
    setScreen('length');
  };

  const handleCompanionFamilyOpen = async () => {
    setFamilyLoading(true);
    setScreen('familyPicker');
    try {
      const { data } = await apiClient.get<{ members: FamilyMember[] }>('/family/members');
      setFamilyMembers(data.members || []);
    } catch {
      setFamilyMembers([]);
    } finally {
      setFamilyLoading(false);
    }
  };

  const handleCompanionFamilyMember = (memberName: string) => {
    setCompanion({ type: 'family', memberName });
    setScreen('length');
  };

  const handleCompanionFriendConfirm = () => {
    const name = friendName.trim().slice(0, 40);
    if (!name) return;
    setCompanion({ type: 'friend', friendName: name });
    setShowFriendInput(false);
    setFriendName('');
    setScreen('length');
  };

  const handleCompanionNone = () => {
    setCompanion(null);
    setScreen('length');
  };

  const handleLength = (len: 'short' | 'medium' | 'long') => {
    setStoryLength(len);
    setScreen('narrator');
  };

  const handleNarrator = async (voice: TTSVoice) => {
    setNarratorVoice(voice);
    setScreen('loading');
    await generate(voice);
  };

  const generate = async (voice: TTSVoice) => {
    if (!childId) return;
    try {
      const characters: StoryCharacter[] = [];
      let characterPhrase = '';
      if (isChildMain && child?.name) {
        characters.push({ name: child.name });
        characterPhrase = `featuring ${child.name}`;
      } else if (character === 'A brave animal') {
        characterPhrase = 'about a brave animal character (Lumi, please invent the animal and give it a name)';
      } else if (character === 'A magical creature') {
        characterPhrase = 'about a magical creature character (Lumi, please invent the creature and give it a name)';
      } else if (character === 'You choose, Lumi!' || character === '') {
        characterPhrase = '(Lumi, please invent a wonderful main character)';
      } else {
        characters.push({ name: character });
        characterPhrase = `featuring ${character}`;
      }

      // Build companion additions to the theme prompt
      let companionPhrase = '';
      let includeFamily = false;
      let companionDescription: string | undefined;
      if (companion?.type === 'yoluno') {
        companionPhrase = ` with ${companion.name} as a companion character`;
        characters.push({ name: companion.name });
        companionDescription = companion.description;
      } else if (companion?.type === 'family') {
        companionPhrase = ` with ${companion.memberName} as a supporting character`;
        includeFamily = true;
      } else if (companion?.type === 'friend') {
        companionPhrase = ` with ${companion.friendName} as a friend character`;
        characters.push({ name: companion.friendName });
      }

      const themeString = `${theme} ${characterPhrase}${companionPhrase} set in ${setting.toLowerCase()}`;
      const mood = themeToMood[theme] || 'adventurous';

      const story = await generateStory({
        childProfileId: childId,
        theme: themeString,
        characters,
        characterDescription,
        companionDescription,
        mood,
        values: [],
        storyLength,
        includeFamily,
        narratorVoice: voice,
        createdBy: 'parent',
      });

      // Invalidate stories cache so the list refreshes
      queryClient.invalidateQueries({ queryKey: queryKeys.stories.all });

      toast.success(`"${story.title}" created! Illustrations are being drawn in the background.`);
      navigate(exitTo);
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message;
      toast.error(serverMsg || err?.message || 'Failed to create story — please try again');
      navigate(exitTo);
    }
  };

  if (childLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: 'linear-gradient(165deg, #F3EFF8 0%, #E8F6F4 30%, #FDF6E8 70%, #FEF0EA 100%)',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div className="text-center">
          <div style={{ fontSize: 48 }} className="animate-bounce mb-4">📖</div>
          <p style={{ fontSize: 16, color: '#B8A5D4', fontWeight: 600 }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{
        background: 'linear-gradient(165deg, #F3EFF8 0%, #E8F6F4 30%, #FDF6E8 70%, #FEF0EA 100%)',
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        className="bg-white rounded-2xl p-8 max-w-md w-full text-center"
        style={{ boxShadow: '0 16px 48px rgba(42,41,38,0.15)' }}
      >
        {/* Lumi avatar */}
        <div className="mb-4" style={{ animation: 'lumiBreath 4s ease-in-out infinite' }}>
          <img
            src={lumiStories}
            alt="Lumi"
            className="mx-auto drop-shadow-2xl"
            style={{ width: 80, height: 80, objectFit: 'contain' }}
          />
        </div>

        {child && (
          <p className="mb-3" style={{ fontSize: 13, color: '#9B978E' }}>
            Creating a story for <strong style={{ color: '#B8A5D4' }}>{child.name}</strong>
          </p>
        )}

        {/* Theme */}
        {screen === 'theme' && (
          <>
            <p className="mb-6" style={{ fontSize: 18, fontWeight: 600, color: '#2A2926' }}>
              What kind of story shall we make today?
            </p>
            <div className="flex flex-col gap-2">
              {THEME_OPTIONS.map((opt) => (
                <OptionButton key={opt} label={opt} onClick={() => handleTheme(opt)} />
              ))}
            </div>
            <CancelButton onClick={handleClose} />
          </>
        )}

        {/* Character */}
        {screen === 'character' && !showCustomCharacterInput && (
          <>
            <p className="mb-6" style={{ fontSize: 18, fontWeight: 600, color: '#2A2926' }}>
              Who's the main character?
            </p>
            <div className="flex flex-col gap-2">
              {getCharacterOptions(child?.name).map((opt) => (
                <OptionButton key={opt} label={opt} onClick={() => handleCharacter(opt)} />
              ))}
            </div>
            <BackCancel onBack={() => setScreen('theme')} onCancel={handleClose} />
          </>
        )}

        {/* Custom character input */}
        {screen === 'character' && showCustomCharacterInput && (
          <>
            <p className="mb-4" style={{ fontSize: 18, fontWeight: 600, color: '#2A2926' }}>
              What's their name?
            </p>
            <input
              type="text"
              value={customCharacterName}
              onChange={(e) => setCustomCharacterName(e.target.value)}
              placeholder="Type a name..."
              autoFocus
              maxLength={40}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCustomCharacterConfirm(); }}
              className="w-full rounded-xl px-4 py-3 mb-4 outline-none"
              style={{ border: '1.5px solid #E8E6E1', fontSize: 15, color: '#2A2926', background: '#F5F3EE' }}
            />
            <button
              onClick={handleCustomCharacterConfirm}
              disabled={!customCharacterName.trim()}
              className="w-full py-3 rounded-xl text-white font-semibold transition hover:opacity-90 disabled:opacity-40"
              style={{ background: '#B8A5D4', fontSize: 15, border: 'none' }}
            >
              Continue →
            </button>
            <BackCancel onBack={() => setShowCustomCharacterInput(false)} onCancel={handleClose} />
          </>
        )}

        {/* Setting */}
        {screen === 'setting' && (
          <>
            <p className="mb-6" style={{ fontSize: 18, fontWeight: 600, color: '#2A2926' }}>
              Where does it happen?
            </p>
            <div className="flex flex-col gap-2">
              {SETTING_OPTIONS.map((opt) => (
                <OptionButton key={opt} label={opt} onClick={() => handleSetting(opt)} />
              ))}
            </div>
            <BackCancel onBack={() => setScreen('character')} onCancel={handleClose} />
          </>
        )}

        {/* Who should be in this story? */}
        {screen === 'companions' && (
          <>
            <p className="mb-2" style={{ fontSize: 18, fontWeight: 600, color: '#2A2926' }}>
              Who should be in this story?
            </p>
            <p className="mb-5" style={{ fontSize: 13, color: '#9B978E', fontStyle: 'italic' }}>
              Pick a Yoluno character to join the adventure, or include a family member
            </p>

            {/* Yoluno character cards */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {YOLUNO_CHARACTERS.map((yc) => (
                <button
                  key={yc.name}
                  onClick={() => handleCompanionYoluno(yc)}
                  className="flex flex-col items-center p-2 rounded-xl transition-all hover:scale-105"
                  style={{
                    background: '#FFFFFF',
                    border: `1.5px solid ${yc.color}`,
                    boxShadow: `0 2px 8px ${yc.color}30`,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    className="rounded-full flex items-center justify-center mb-1 overflow-hidden"
                    style={{ width: 48, height: 48, background: yc.bg }}
                  >
                    <img src={yc.image} alt={yc.name} style={{ width: 40, height: 40, objectFit: 'contain' }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: yc.color }}>{yc.name}</span>
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-2 mb-3">
              <div style={{ flex: 1, height: 1, background: '#E8E6E1' }} />
              <span style={{ fontSize: 11, color: '#9B978E', whiteSpace: 'nowrap' }}>or</span>
              <div style={{ flex: 1, height: 1, background: '#E8E6E1' }} />
            </div>

            {!showFriendInput ? (
              <div className="flex flex-col gap-2">
                <CompanionButton label="👨‍👩‍👧 A family member" hoverColor="#D4A843" hoverBg="#FDF6E8" onClick={handleCompanionFamilyOpen} />
                <CompanionButton label="👫 A friend" hoverColor="#3ECDC6" hoverBg="#E8F6F4" onClick={() => setShowFriendInput(true)} />
                <CompanionButton label="Just the main character" hoverColor="#B8A5D4" hoverBg="#F3EFF8" onClick={handleCompanionNone} muted />
              </div>
            ) : (
              <>
                <p className="mb-3" style={{ fontSize: 15, fontWeight: 600, color: '#2A2926' }}>
                  What's their name?
                </p>
                <input
                  type="text"
                  value={friendName}
                  onChange={(e) => setFriendName(e.target.value)}
                  placeholder="Type a name..."
                  autoFocus
                  maxLength={40}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCompanionFriendConfirm(); }}
                  className="w-full rounded-xl px-4 py-3 mb-3 outline-none"
                  style={{ border: '1.5px solid #E8E6E1', fontSize: 15, color: '#2A2926', background: '#F5F3EE' }}
                />
                <button
                  onClick={handleCompanionFriendConfirm}
                  disabled={!friendName.trim()}
                  className="w-full py-3 rounded-xl text-white font-semibold transition hover:opacity-90 disabled:opacity-40"
                  style={{ background: '#3ECDC6', fontSize: 15, border: 'none' }}
                >
                  Continue →
                </button>
                <button onClick={() => setShowFriendInput(false)} className="mt-2" style={{ fontSize: 13, color: '#9B978E', background: 'none', border: 'none', cursor: 'pointer' }}>
                  ← Back
                </button>
              </>
            )}
            {!showFriendInput && <BackCancel onBack={() => setScreen('setting')} onCancel={handleClose} />}
          </>
        )}

        {/* Family member picker */}
        {screen === 'familyPicker' && (
          <>
            <p className="mb-6" style={{ fontSize: 18, fontWeight: 600, color: '#2A2926' }}>
              Which family member?
            </p>
            {familyLoading ? (
              <p style={{ fontSize: 14, color: '#9B978E' }}>Loading family members...</p>
            ) : familyMembers.length === 0 ? (
              <>
                <p style={{ fontSize: 14, color: '#9B978E', marginBottom: 16 }}>
                  No family members added yet. Add them in your family settings first.
                </p>
                <button
                  onClick={() => setScreen('companions')}
                  className="w-full py-3 rounded-xl font-semibold"
                  style={{ background: '#F5F3EE', color: '#2A2926', border: 'none', fontSize: 15, cursor: 'pointer' }}
                >
                  Go back
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2">
                {familyMembers.map((m) => (
                  <button
                    key={m.name}
                    onClick={() => handleCompanionFamilyMember(m.name)}
                    className="w-full py-3 px-4 rounded-xl text-left transition-all"
                    style={{ background: '#FFFFFF', border: '1.5px solid #E8E6E1', fontSize: 15, color: '#2A2926', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = '#D4A843';
                      (e.currentTarget as HTMLElement).style.background = '#FDF6E8';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = '#E8E6E1';
                      (e.currentTarget as HTMLElement).style.background = '#FFFFFF';
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{m.name}</span>
                    <span style={{ color: '#9B978E', fontSize: 13 }}> · {m.relationship}</span>
                  </button>
                ))}
              </div>
            )}
            <BackCancel onBack={() => setScreen('companions')} onCancel={handleClose} />
          </>
        )}

        {/* Length */}
        {screen === 'length' && (
          <>
            <p className="mb-6" style={{ fontSize: 18, fontWeight: 600, color: '#2A2926' }}>
              How long should it be?
            </p>
            <div className="flex flex-col gap-2">
              {LENGTH_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => handleLength(opt.key)}
                  className="w-full py-3 px-4 rounded-xl text-left transition-all"
                  style={{ background: '#FFFFFF', border: '1.5px solid #E8E6E1', cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = '#B8A5D4';
                    (e.currentTarget as HTMLElement).style.background = '#F3EFF8';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = '#E8E6E1';
                    (e.currentTarget as HTMLElement).style.background = '#FFFFFF';
                  }}
                >
                  <p style={{ fontSize: 15, fontWeight: 600, color: '#2A2926' }}>{opt.label}</p>
                  <p style={{ fontSize: 12, color: '#9B978E', marginTop: 2 }}>{opt.desc}</p>
                </button>
              ))}
            </div>
            <BackCancel onBack={() => setScreen('companions')} onCancel={handleClose} />
          </>
        )}

        {/* Narrator */}
        {screen === 'narrator' && (
          <>
            <p className="mb-2" style={{ fontSize: 18, fontWeight: 600, color: '#2A2926' }}>
              Who should read it?
            </p>
            <p className="mb-6" style={{ fontSize: 13, color: '#9B978E', fontStyle: 'italic' }}>
              Pick a narrator voice
            </p>
            <div className="grid grid-cols-2 gap-3">
              {NARRATORS.map((n) => (
                <button
                  key={n.voice}
                  onClick={() => handleNarrator(n.voice)}
                  className="flex flex-col items-center p-4 rounded-xl transition-all hover:scale-105"
                  style={{
                    background: '#FFFFFF',
                    border: `1.5px solid ${n.color}`,
                    boxShadow: `0 2px 8px ${n.color}30`,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    className="rounded-full flex items-center justify-center mb-2 overflow-hidden"
                    style={{ width: 56, height: 56, background: n.bg }}
                  >
                    <img src={n.image} alt={n.name} style={{ width: 48, height: 48, objectFit: 'contain' }} />
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 600, color: n.color }}>{n.name}</span>
                </button>
              ))}
            </div>
            <BackCancel onBack={() => setScreen('length')} onCancel={handleClose} />
          </>
        )}

        {/* Loading */}
        {screen === 'loading' && (
          <>
            <p className="mb-2" style={{ fontSize: 22, fontWeight: 600, color: '#2A2926' }}>
              Lumi is dreaming up the story...
            </p>
            <p className="mb-6" style={{ fontSize: 14, color: '#9B978E', fontStyle: 'italic', minHeight: 20 }}>
              {LOADING_MESSAGES[loadingMsgIndex]}
            </p>
            <div className="flex gap-1.5 justify-center">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#B8A5D4',
                    animation: `dotPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
            <p className="mt-6" style={{ fontSize: 12, color: '#9B978E' }}>
              This usually takes about a minute. Don't close the window!
            </p>
          </>
        )}
      </div>

      <style>{`
        @keyframes lumiBreath {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}

// ─── Shared UI helpers ──────────────────────────────────────────────────────

function OptionButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full py-3 px-4 rounded-xl text-left transition-all"
      style={{ background: '#FFFFFF', border: '1.5px solid #E8E6E1', fontSize: 15, color: '#2A2926', cursor: 'pointer' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = '#B8A5D4';
        (e.currentTarget as HTMLElement).style.background = '#F3EFF8';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = '#E8E6E1';
        (e.currentTarget as HTMLElement).style.background = '#FFFFFF';
      }}
    >
      {label}
    </button>
  );
}

function CancelButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="mt-4" style={{ fontSize: 13, color: '#9B978E', background: 'none', border: 'none', cursor: 'pointer' }}>
      Cancel
    </button>
  );
}

function CompanionButton({ label, hoverColor, hoverBg, onClick, muted }: { label: string; hoverColor: string; hoverBg: string; onClick: () => void; muted?: boolean }) {
  return (
    <button
      onClick={onClick}
      className="w-full py-3 px-4 rounded-xl text-left transition-all"
      style={{ background: '#FFFFFF', border: '1.5px solid #E8E6E1', fontSize: 15, color: muted ? '#9B978E' : '#2A2926', cursor: 'pointer' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = hoverColor;
        (e.currentTarget as HTMLElement).style.background = hoverBg;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = '#E8E6E1';
        (e.currentTarget as HTMLElement).style.background = '#FFFFFF';
      }}
    >
      {label}
    </button>
  );
}

function BackCancel({ onBack, onCancel }: { onBack: () => void; onCancel: () => void }) {
  return (
    <div className="flex justify-between mt-4 px-2">
      <button onClick={onBack} style={{ fontSize: 13, color: '#B8A5D4', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
        ← Back
      </button>
      <button onClick={onCancel} style={{ fontSize: 13, color: '#9B978E', background: 'none', border: 'none', cursor: 'pointer' }}>
        Cancel
      </button>
    </div>
  );
}
